// SkipEnquiryDetailPage — /skip/enquiries/:id.
//
// Two-pane layout: enquiry details + contact log on the left, action
// sidebar on the right. Cancel + Convert open dedicated modals; the
// remaining workflow (status flips, follow-up date) is on the edit page.

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import {
  useCancelSkipEnquiry,
  useSkipEnquiry,
} from '../hooks/useSkipEnquiries';
import { SkipEnquiryStatusBadge } from './components/SkipEnquiryStatusBadge';
import { SkipEnquiryContactLog } from './components/SkipEnquiryContactLog';
import { SkipEnquiryCancelModal } from './components/SkipEnquiryCancelModal';
import { AttachmentSection } from '../components/AttachmentSection';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './SkipEnquiryDetailPage.module.css';

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtAddress(...parts: Array<string | null | undefined>): string {
  return parts.filter((p) => typeof p === 'string' && p.length > 0).join(', ') || '—';
}

export function SkipEnquiryDetailPage(): JSX.Element {
  const { id: encId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const enquiryQuery = useSkipEnquiry(encId);
  const cancel = useCancelSkipEnquiry(encId);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  if (enquiryQuery.isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="Loading enquiry…" />
      </div>
    );
  }

  if (enquiryQuery.isError || !enquiryQuery.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Enquiry not found" />
        <Card>
          <p>This enquiry could not be loaded.</p>
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/skip/enquiries')}>
            Back to Enquiries
          </Button>
        </Card>
      </div>
    );
  }

  const enquiry = enquiryQuery.data.enquiry;
  const isTerminal =
    enquiry.status === 'converted' ||
    enquiry.status === 'cancelled' ||
    enquiry.status === 'expired';

  function handleCancel(reason: string) {
    setCancelError(null);
    cancel.mutate(
      { reason },
      {
        onSuccess: () => setCancelOpen(false),
        onError: (err) => setCancelError(extractErrorMessage(err)),
      },
    );
  }

  // Bug 4 — Convert to Job navigates to ProcessEnquiryPage instead of
  // opening a same-page modal. The full-form conversion flow lives at
  // /skip/enquiries/:enc_id/process (covers required size, unit and
  // delivery date pre-flight, plus optional driver / payment fields).
  function handleConvertNavigate() {
    navigate(`/skip/enquiries/${encodeURIComponent(encId)}/process`);
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Enquiry ${enquiry.enquiry_number}`}
        subtitle="View and manage enquiry details"
        actions={
          <div className={styles.headerActions}>
            <Button
              variant="secondary"
              icon={ArrowLeft}
              onClick={() => navigate('/skip/enquiries')}
            >
              Back
            </Button>
            <Button
              variant="primary"
              icon={Pencil}
              disabled={isTerminal}
              onClick={() => navigate(`/skip/enquiries/${encodeURIComponent(encId)}/edit`)}
            >
              Edit
            </Button>
          </div>
        }
      />

      <div className={styles.layout}>
        <div className={styles.main}>
          <Card>
            <header className={styles.sectionHeader}>
              <h2>Customer Details</h2>
              <div className={styles.badges}>
                {enquiry.is_priority ? (
                  <span className={styles.priorityChip}>PRIORITY</span>
                ) : null}
                <SkipEnquiryStatusBadge status={enquiry.status} />
              </div>
            </header>
            <dl className={styles.dl}>
              <dt>Customer</dt>
              <dd>
                {enquiry.customer_uuid ? (
                  <Link to={`/skip/customers/${encodeURIComponent(enquiry.customer_uuid)}`}>
                    {enquiry.customer_name}
                  </Link>
                ) : (
                  enquiry.customer_name
                )}
              </dd>
              <dt>Phone</dt>
              <dd>{enquiry.customer_phone}</dd>
              <dt>Secondary Phone</dt>
              <dd>{enquiry.customer_phone2 ?? '—'}</dd>
              <dt>Email</dt>
              <dd>{enquiry.customer_email ?? '—'}</dd>
              <dt>Postcode</dt>
              <dd>{enquiry.customer_postcode}</dd>
              <dt>Site / Address</dt>
              <dd>
                {enquiry.site_enc_id != null
                  ? fmtAddress(
                      enquiry.site_label,
                      enquiry.site_address_line1,
                      enquiry.site_address_line2,
                      enquiry.site_city,
                      enquiry.site_postcode,
                    )
                  : fmtAddress(
                      enquiry.customer_address_line1,
                      enquiry.customer_address_line2,
                      enquiry.customer_city,
                      enquiry.customer_postcode,
                    )}
              </dd>
            </dl>
          </Card>

          <Card>
            <header className={styles.sectionHeader}>
              <h2>Stock / Job Details</h2>
            </header>
            <dl className={styles.dl}>
              <dt>Stock Type</dt>
              <dd>{enquiry.skip_stock_type_name ?? enquiry.skip_type ?? '—'}</dd>
              <dt>Job Type</dt>
              <dd>{enquiry.job_type ? enquiry.job_type.replaceAll('_', ' ') : '—'}</dd>
              <dt>Estimated Price</dt>
              <dd>
                {enquiry.estimated_price !== null
                  ? `£${enquiry.estimated_price.toFixed(2)}`
                  : '—'}
              </dd>
              <dt>Preferred Delivery</dt>
              <dd>{fmtDate(enquiry.preferred_delivery_date)}</dd>
            </dl>
          </Card>

          <Card>
            <header className={styles.sectionHeader}>
              <h2>Follow-up</h2>
            </header>
            <dl className={styles.dl}>
              <dt>Follow-up Date</dt>
              <dd>{fmtDate(enquiry.follow_up_date)}</dd>
              <dt>Last Contact</dt>
              <dd>{fmtDate(enquiry.last_contact_date)}</dd>
              <dt>Contact Attempts</dt>
              <dd>{enquiry.contact_attempts}</dd>
            </dl>
          </Card>

          <Card>
            <header className={styles.sectionHeader}>
              <h2>Notes</h2>
            </header>
            <p className={styles.notes}>
              {enquiry.notes ?? <em className={styles.empty}>No notes.</em>}
            </p>
            {enquiry.is_priority && enquiry.priority_notes ? (
              <>
                <h3 className={styles.subheading}>Priority notes</h3>
                <p className={styles.notes}>{enquiry.priority_notes}</p>
              </>
            ) : null}
          </Card>

          <SkipEnquiryContactLog enquiryEncId={encId} readOnly={isTerminal} />

          <Card>
            <header className={styles.sectionHeader}>
              <h2>Attachments</h2>
            </header>
            <AttachmentSection
              kind="enquiry"
              encId={encId}
              readOnly={isTerminal}
            />
          </Card>

          {enquiry.status === 'cancelled' && enquiry.cancellation_reason ? (
            <Card>
              <header className={styles.sectionHeader}>
                <h2>Cancellation Reason</h2>
              </header>
              <p className={styles.notes}>{enquiry.cancellation_reason}</p>
            </Card>
          ) : null}

          {enquiry.status === 'converted' && enquiry.converted_skip_job_uuid ? (
            <Card>
              <header className={styles.sectionHeader}>
                <h2>Converted to Job</h2>
              </header>
              <p>
                <Link
                  to={`/skip/jobs/${encodeURIComponent(enquiry.converted_skip_job_uuid)}`}
                  className={styles.cellLink}
                >
                  View resulting job →
                </Link>
              </p>
            </Card>
          ) : null}
        </div>

        <aside className={styles.sidebar}>
          <Card>
            <header className={styles.sectionHeader}>
              <h2>Actions</h2>
            </header>
            {!isTerminal ? (
              <div className={styles.actions}>
                <Button
                  variant="primary"
                  onClick={handleConvertNavigate}
                >
                  Convert to Job
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setCancelOpen(true)}
                  disabled={cancel.isPending}
                >
                  Cancel Enquiry
                </Button>
              </div>
            ) : (
              <p className={styles.terminalHint}>
                This enquiry is {enquiry.status} and has no available actions.
              </p>
            )}
          </Card>
        </aside>
      </div>

      <SkipEnquiryCancelModal
        isOpen={cancelOpen}
        onClose={() => {
          setCancelOpen(false);
          setCancelError(null);
        }}
        enquiryReference={enquiry.enquiry_number}
        onConfirm={handleCancel}
        isLoading={cancel.isPending}
        errorMessage={cancelError}
      />
    </div>
  );
}
